'use client';

import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { useOrganizationProfile } from '@/hooks/useOrganizationProfile';
import { useToast } from '@/hooks/use-toast';
import { 
  User, 
  Building2, 
  Phone, 
  Mail, 
  Settings, 
  Shield,
  Camera,
  MapPin,
  Globe
} from 'lucide-react';

export default function AdminProfilePage() {
  const { 
    profile: orgProfile, 
    loading: orgLoading, 
    createProfile: createOrgProfile, 
    updateProfile: updateOrgProfile 
  } = useOrganizationProfile();
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  
  const [userFormData, setUserFormData] = useState({
    job_title: '',
    department: '',
    phone_number: '',
    preferred_contact_method: 'email',
    bio: '',
    profile_image_url: '',
    admin_level: 'system_admin',
    specializations: [] as string[]
  });

  const [orgFormData, setOrgFormData] = useState({
    description: '',
    industry: '',
    company_size: '',
    headquarters_location: '',
    website_url: '',
    founded_year: null as number | null,
    annual_revenue_range: '',
    typical_project_types: [] as string[],
    contact_email: '',
    preferred_payment_terms: ''
  });

  React.useEffect(() => {
    if (orgProfile) {
      setOrgFormData({
        description: orgProfile.description || '',
        industry: orgProfile.industry || '',
        company_size: orgProfile.company_size || '',
        headquarters_location: orgProfile.headquarters_location || '',
        website_url: orgProfile.website_url || '',
        founded_year: orgProfile.founded_year || null,
        annual_revenue_range: orgProfile.annual_revenue_range || '',
        typical_project_types: Array.isArray(orgProfile.typical_project_types) ? orgProfile.typical_project_types : [],
        contact_email: orgProfile.contact_email || '',
        preferred_payment_terms: orgProfile.preferred_payment_terms || ''
      });
    }
  }, [orgProfile]);

  const handleUserSave = async () => {
    try {
      // Note: Since we don't have a specific admin profile table, 
      // we'll just show a success message for now
      setIsEditing(false);
      toast({
        title: "Success",
        description: "Admin profile updated successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update admin profile",
        variant: "destructive",
      });
    }
  };

  const handleOrgSave = async () => {
    try {
      if (orgProfile) {
        await updateOrgProfile(orgFormData);
      } else {
        await createOrgProfile(orgFormData);
      }
      setIsEditing(false);
      toast({
        title: "Success",
        description: "Organization profile updated successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update organization profile",
        variant: "destructive",
      });
    }
  };

  if (orgLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/4"></div>
          <div className="h-64 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Shield className="h-8 w-8 text-primary" />
              Admin Profile
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage your administrative profile and system preferences
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="default" className="bg-primary">
              <Shield className="h-3 w-3 mr-1" />
              System Administrator
            </Badge>
            <Button
              onClick={() => isEditing ? handleUserSave() : setIsEditing(true)}
              disabled={orgLoading}
            >
              {isEditing ? 'Save Changes' : 'Edit Profile'}
            </Button>
          </div>
        </div>

        {/* Profile Content */}
        <Tabs defaultValue="user" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="user">User Profile</TabsTrigger>
            <TabsTrigger value="organization">Organization Profile</TabsTrigger>
          </TabsList>

          {/* User Profile Tab */}
          <TabsContent value="user">
            <div className="space-y-6">
              {/* Personal Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="h-5 w-5" />
                    Personal Information
                  </CardTitle>
                  <CardDescription>
                    Your basic information and administrative details
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Profile Photo */}
                  <div className="flex items-center gap-4">
                    <Avatar className="h-20 w-20">
                      <AvatarImage src={userFormData.profile_image_url} />
                      <AvatarFallback>
                        <Shield className="h-8 w-8" />
                      </AvatarFallback>
                    </Avatar>
                    {isEditing && (
                      <div className="space-y-2">
                        <Button variant="outline" size="sm">
                          <Camera className="h-4 w-4 mr-2" />
                          Upload Photo
                        </Button>
                        <p className="text-xs text-muted-foreground">
                          JPG, PNG up to 5MB
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="job_title">Job Title</Label>
                      <Input
                        id="job_title"
                        value={userFormData.job_title}
                        onChange={(e) => setUserFormData(prev => ({ ...prev, job_title: e.target.value }))}
                        disabled={!isEditing}
                        placeholder="e.g., System Administrator"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="department">Department</Label>
                      <Input
                        id="department"
                        value={userFormData.department}
                        onChange={(e) => setUserFormData(prev => ({ ...prev, department: e.target.value }))}
                        disabled={!isEditing}
                        placeholder="e.g., IT Administration"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="phone_number">Phone Number</Label>
                      <Input
                        id="phone_number"
                        value={userFormData.phone_number}
                        onChange={(e) => setUserFormData(prev => ({ ...prev, phone_number: e.target.value }))}
                        disabled={!isEditing}
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="preferred_contact">Preferred Contact Method</Label>
                      <Select
                        value={userFormData.preferred_contact_method}
                        onValueChange={(value) => setUserFormData(prev => ({ ...prev, preferred_contact_method: value }))}
                        disabled={!isEditing}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="email">Email</SelectItem>
                          <SelectItem value="phone">Phone</SelectItem>
                          <SelectItem value="both">Both</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="admin_level">Administrative Level</Label>
                      <Select
                        value={userFormData.admin_level}
                        onValueChange={(value) => setUserFormData(prev => ({ ...prev, admin_level: value }))}
                        disabled={!isEditing}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="system_admin">System Administrator</SelectItem>
                          <SelectItem value="super_admin">Super Administrator</SelectItem>
                          <SelectItem value="platform_admin">Platform Administrator</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="bio">Professional Bio</Label>
                    <Textarea
                      id="bio"
                      value={userFormData.bio}
                      onChange={(e) => setUserFormData(prev => ({ ...prev, bio: e.target.value }))}
                      disabled={!isEditing}
                      placeholder="Brief professional summary and administrative experience..."
                      maxLength={500}
                      rows={4}
                    />
                    <p className="text-xs text-muted-foreground">
                      {userFormData.bio.length}/500 characters
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Administrative Settings */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="h-5 w-5" />
                    Administrative Preferences
                  </CardTitle>
                  <CardDescription>
                    Configure your administrative preferences and system settings
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Notification Preferences</Label>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm">System Alerts</span>
                          <Badge variant="outline">Enabled</Badge>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm">User Management</span>
                          <Badge variant="outline">Enabled</Badge>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm">Security Alerts</span>
                          <Badge variant="outline">Enabled</Badge>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Access Permissions</Label>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm">User Management</span>
                          <Badge variant="default">Full Access</Badge>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm">System Configuration</span>
                          <Badge variant="default">Full Access</Badge>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm">Analytics & Reports</span>
                          <Badge variant="default">Full Access</Badge>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Organization Profile Tab */}
          <TabsContent value="organization">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Organization Information
                </CardTitle>
                <CardDescription>
                  Manage your organization's profile and business details
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="industry">Industry</Label>
                    <Select
                      value={orgFormData.industry}
                      onValueChange={(value) => setOrgFormData(prev => ({ ...prev, industry: value }))}
                      disabled={!isEditing}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select industry" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="technology">Technology</SelectItem>
                        <SelectItem value="healthcare">Healthcare</SelectItem>
                        <SelectItem value="finance">Finance</SelectItem>
                        <SelectItem value="manufacturing">Manufacturing</SelectItem>
                        <SelectItem value="construction">Construction</SelectItem>
                        <SelectItem value="education">Education</SelectItem>
                        <SelectItem value="retail">Retail</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="company_size">Organization Size</Label>
                    <Select
                      value={orgFormData.company_size}
                      onValueChange={(value) => setOrgFormData(prev => ({ ...prev, company_size: value }))}
                      disabled={!isEditing}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select organization size" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1-10">1-10 employees</SelectItem>
                        <SelectItem value="11-50">11-50 employees</SelectItem>
                        <SelectItem value="51-200">51-200 employees</SelectItem>
                        <SelectItem value="201-1000">201-1000 employees</SelectItem>
                        <SelectItem value="1000+">1000+ employees</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="headquarters_location">Headquarters Location</Label>
                    <Input
                      id="headquarters_location"
                      value={orgFormData.headquarters_location}
                      onChange={(e) => setOrgFormData(prev => ({ ...prev, headquarters_location: e.target.value }))}
                      disabled={!isEditing}
                      placeholder="e.g., San Francisco, CA"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="website_url">Website URL</Label>
                    <Input
                      id="website_url"
                      type="url"
                      value={orgFormData.website_url}
                      onChange={(e) => setOrgFormData(prev => ({ ...prev, website_url: e.target.value }))}
                      disabled={!isEditing}
                      placeholder="https://www.organization.com"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="founded_year">Founded Year</Label>
                    <Input
                      id="founded_year"
                      type="number"
                      value={orgFormData.founded_year || ''}
                      onChange={(e) => setOrgFormData(prev => ({ ...prev, founded_year: e.target.value ? parseInt(e.target.value) : null }))}
                      disabled={!isEditing}
                      placeholder="2020"
                      min="1800"
                      max={new Date().getFullYear()}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="annual_revenue_range">Annual Revenue Range</Label>
                    <Select
                      value={orgFormData.annual_revenue_range}
                      onValueChange={(value) => setOrgFormData(prev => ({ ...prev, annual_revenue_range: value }))}
                      disabled={!isEditing}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select revenue range" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="under_1m">Under $1M</SelectItem>
                        <SelectItem value="1m_5m">$1M - $5M</SelectItem>
                        <SelectItem value="5m_25m">$5M - $25M</SelectItem>
                        <SelectItem value="25m_100m">$25M - $100M</SelectItem>
                        <SelectItem value="over_100m">Over $100M</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="contact_email">Contact Email</Label>
                    <Input
                      id="contact_email"
                      type="email"
                      value={orgFormData.contact_email}
                      onChange={(e) => setOrgFormData(prev => ({ ...prev, contact_email: e.target.value }))}
                      disabled={!isEditing}
                      placeholder="contact@organization.com"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="preferred_payment_terms">Preferred Payment Terms</Label>
                    <Select
                      value={orgFormData.preferred_payment_terms}
                      onValueChange={(value) => setOrgFormData(prev => ({ ...prev, preferred_payment_terms: value }))}
                      disabled={!isEditing}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select payment terms" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="net_15">Net 15</SelectItem>
                        <SelectItem value="net_30">Net 30</SelectItem>
                        <SelectItem value="net_45">Net 45</SelectItem>
                        <SelectItem value="net_60">Net 60</SelectItem>
                        <SelectItem value="immediate">Immediate</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Organization Description</Label>
                  <Textarea
                    id="description"
                    value={orgFormData.description}
                    onChange={(e) => setOrgFormData(prev => ({ ...prev, description: e.target.value }))}
                    disabled={!isEditing}
                    placeholder="Describe your organization, mission, and what you do..."
                    maxLength={1000}
                    rows={4}
                  />
                  <p className="text-xs text-muted-foreground">
                    {orgFormData.description.length}/1000 characters
                  </p>
                </div>

                {isEditing && (
                  <div className="flex gap-2">
                    <Button onClick={handleOrgSave}>
                      Save Organization Profile
                    </Button>
                    <Button variant="outline" onClick={() => setIsEditing(false)}>
                      Cancel
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}